import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelAccount,
  getCancellationPreview,
  sendCancellationCode,
  type CancellationPreview,
} from '../../../api/user';
import { useAuthStore } from '../../../stores/authStore';
import { handleApiError, showErrorNotification, ErrorTypes } from '../../../utils/errorHandler';

/** 与后端 sendCode 的限发窗口一致（verification-code 通道 60 秒） */
const RESEND_SECONDS = 60;

/**
 * 账号注销（一次确认、无冷静期）。
 *
 * 页面结构照后端裁定来：先把「会删掉什么、会留下什么」摆出来（preview 是只读接口，
 * 可以反复刷新），用户勾选知晓后再走「验证码 + 口令」双确认。
 * 刻意不用 window.confirm 之类的系统弹窗：那等于让用户在看不见清单的情况下点确认。
 */
export default function AccountCancellationCard() {
  const { signOut } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<CancellationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  // 验证码「已发送」是成功提示，不能复用错误文案的红框：
  // 用户看到红框会以为没发出去，于是再点一次，撞上 60 秒限发反而更慢。
  const [sendHint, setSendHint] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const id = setInterval(() => setCountdown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setPreview(await getCancellationPreview());
    } catch (error) {
      // 清单拉不到就不许继续：没有清单的确认等于没确认
      setPreview(null);
      setLoadError(handleApiError(error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStart = async () => {
    setOpen(true);
    setErrMsg('');
    await loadPreview();
  };

  const handleSendCode = async () => {
    setSending(true);
    setErrMsg('');
    setSendHint('');
    try {
      const res = await sendCancellationCode();
      setSendHint(res?.message ?? '验证码已发送，请查收短信');
      setCountdown(RESEND_SECONDS);
    } catch (error) {
      const info = handleApiError(error);
      setErrMsg(info.message);
      showErrorNotification(info);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!acknowledged) {
      setErrMsg('请先勾选「我已知晓」后再提交');
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      setErrMsg('请输入 6 位短信验证码');
      return;
    }
    setSubmitting(true);
    setErrMsg('');
    try {
      const res = await cancelAccount({ code: code.trim(), password: password || undefined });
      setDone(res?.mode === 'erase' ? '账号已注销，档案与本人孤立订单已一并删除' : '账号已注销，客户档案按要求保留');
      await signOut();
      setTimeout(() => {
        window.location.href = '/login';
      }, 2500);
    } catch (error) {
      const info = handleApiError(error);
      setErrMsg(info.message);
      // 验证码错满 5 次会被锁 10 分钟，重新拉一次清单无意义，让用户按提示改正
      if (info.type !== ErrorTypes.AUTHENTICATION) showErrorNotification(info);
    } finally {
      setSubmitting(false);
    }
  };

  const inp =
    'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 transition-colors';

  if (done) {
    return (
      <div className="w-full p-4 bg-teal-50 border border-teal-100 rounded-lg">
        <p className="text-sm font-medium text-teal-700 flex items-center gap-2">
          <i className="ri-checkbox-circle-line" />
          {done}
        </p>
        <p className="text-xs text-teal-600 mt-1">正在返回登录页…</p>
      </div>
    );
  }

  return (
    <div className="w-full sm:max-w-md rounded-lg border border-red-100 bg-red-50/40 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">注销账号</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            注销后无法登录，孤立订单与本人凭证会被删除；被他人绑定的客户档案、双方往来订单按规则保留。
          </p>
        </div>
        {!open && (
          <button
            type="button"
            onClick={handleStart}
            className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 cursor-pointer whitespace-nowrap"
          >
            开始注销
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-4 pt-1">
          {loading && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin" />正在清点你的账号数据…
            </p>
          )}

          {!loading && loadError && (
            <div className="flex items-center justify-between gap-3 p-3 bg-white border border-red-100 rounded-lg">
              <span className="text-sm text-red-600">{loadError}</span>
              <button
                type="button"
                onClick={loadPreview}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                重新获取
              </button>
            </div>
          )}

          {!loading && preview && (
            <div className="bg-white border border-gray-100 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">本次注销的清单</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>删除：{preview.stats.orphanOrders} 笔你本人创建、对方未注册的订单，{preview.stats.orphanCustomers} 个只有你在用的客户档案</li>
                <li>删除：{preview.stats.voucherFiles} 张已上传的付款凭证图片</li>
                <li>
                  保留：{preview.stats.interactiveOrders} 笔对方是注册用户的往来订单，
                  {preview.stats.keptCustomers} 个仍被他人绑定的客户档案
                </li>
              </ul>
              <p className="text-xs font-semibold text-gray-500 pt-1">
                {preview.mode === 'erase'
                  ? '你的账号档案会被整行删除（没有人再引用它）'
                  : '你的登录信息会被清除，作为他人客户档案的部分保留'}
              </p>
              {preview.reasons.length > 0 && (
                <ul className="text-xs text-gray-500 space-y-0.5 list-disc pl-4">
                  {preview.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={loadPreview}
                className="text-xs text-teal-600 hover:underline cursor-pointer"
              >
                刷新清单
              </button>
            </div>
          )}

          {preview && (
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-red-500"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <span>我已知晓：注销不可撤销，系统不设冷静期，提交即生效</span>
              </label>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  账号密码
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="用于确认是本人操作（该账号未设过密码可留空）"
                  autoComplete="current-password"
                  className={inp}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  短信验证码
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={`发送至 ${preview.phone}`}
                    className={inp}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={sending || countdown > 0}
                    className="shrink-0 px-3 py-2.5 text-sm font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {countdown > 0 ? `${countdown}s` : sending ? '发送中' : '获取验证码'}
                  </button>
                </div>
              </div>

              {sendHint && (
                <div className="flex items-center gap-2 p-3 bg-white border border-teal-100 rounded-lg text-sm text-teal-700">
                  <i className="ri-mail-send-line" />
                  {sendHint}
                </div>
              )}

              {errMsg && (
                <div className="flex items-center gap-2 p-3 bg-white border border-red-100 rounded-lg text-sm text-red-600">
                  <i className="ri-error-warning-line" />
                  {errMsg}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !acknowledged}
                  className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      注销中...
                    </span>
                  ) : (
                    '确认注销账号'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setPreview(null);
                    setPassword('');
                    setCode('');
                    setAcknowledged(false);
                    setErrMsg('');
                    setSendHint('');
                  }}
                  className="px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
